"use client";

import { FC, ReactNode } from "react";
import { useFormContext } from "react-hook-form";

import { cx } from "../../../../utils/cx";
import { PunchupFieldLabel } from "../PunchupFieldLabel";
import styles from "./PunchupRadioCardGroup.module.css";

export interface PunchupRadioCardOption<V extends string = string> {
  value: V;
  label: string;
  /** Optional sub-text shown below the label inside each card. */
  description?: string;
  /** Display-only element drawn beside the label (e.g. a `PunchupTag`). Never interactive — the card is a button. */
  badge?: ReactNode;
  /** Display-only element pinned to the card's trailing edge (e.g. a price). Never interactive. */
  aside?: ReactNode;
  /** Display-only muted line under `aside` (e.g. a yearly total). Ignored without `aside`. */
  asideCaption?: ReactNode;
  /** Leading glyph (tile before the label in `inline`, bare on the top row in `stacked`); aria-hidden. */
  icon?: ReactNode;
  /** Muted stat line under the description (e.g. "3 shows at 2 venues"). */
  meta?: string;
  /** Disables this card only (blocks clicks, dims it); the others stay selectable. */
  disabled?: boolean;
}

/** `"auto"` = one column, two at ≥400px container width; `1` pins a single column; `3` is one column, three at ≥600px. */
export type PunchupRadioCardColumns = "auto" | 1 | 3;

/** `"inline"` = icon tile before the label; `"stacked"` = icon (and badge) on a top row above the title. */
export type PunchupRadioCardLayout = "inline" | "stacked";

export interface PunchupRadioCardGroupProps {
  /** react-hook-form field path (e.g. 'custom_field.only_buyer'). */
  name: string;
  options: PunchupRadioCardOption[];
  label?: string;
  required?: boolean;
  /** Disables every card (e.g. while an async save is in flight) — blocks clicks and dims the cards. */
  disabled?: boolean;
  columns?: PunchupRadioCardColumns;
  layout?: PunchupRadioCardLayout;
  /** Longer text whose height every card's description reserves, so swapping descriptions never resizes a card. */
  reserveDescription?: string;
}

export interface PunchupRadioCardsProps<V extends string = string> {
  /** Currently selected value; `null` selects no card. */
  value: V | null;
  onChange: (value: V) => void;
  options: PunchupRadioCardOption<V>[];
  label?: string;
  /** Disables every card (e.g. while an async save is in flight) — blocks clicks and dims the cards. */
  disabled?: boolean;
  columns?: PunchupRadioCardColumns;
  layout?: PunchupRadioCardLayout;
  /** Longer text whose height every card's description reserves, so swapping descriptions never resizes a card. */
  reserveDescription?: string;
}

interface CardBodyProps {
  option: PunchupRadioCardOption;
  reserveDescription?: string;
}

const CardTitle: FC<Pick<PunchupRadioCardOption, "label" | "badge">> = ({
  label,
  badge,
}) =>
  badge ? (
    <span className={styles.cardLabelRow}>
      <span className={styles.cardLabel}>{label}</span>
      {badge}
    </span>
  ) : (
    <span className={styles.cardLabel}>{label}</span>
  );

// Description (with its invisible reserve sizer sharing the grid cell) and meta line.
const CardDetails: FC<CardBodyProps> = ({ option, reserveDescription }) => (
  <>
    {option.description &&
      (reserveDescription ? (
        <span
          className={cx(styles.cardDescription, styles.cardDescriptionReserved)}
        >
          <span>{option.description}</span>
          <span className={styles.cardDescriptionSizer} aria-hidden="true">
            {reserveDescription}
          </span>
        </span>
      ) : (
        <span className={styles.cardDescription}>{option.description}</span>
      ))}
    {option.meta && <span className={styles.cardMeta}>{option.meta}</span>}
  </>
);

// Icon tile before the content column; the badge sits beside the title.
const InlineCardBody: FC<CardBodyProps> = (props) => (
  <>
    {props.option.icon && (
      <span className={styles.cardIcon} aria-hidden="true">
        {props.option.icon}
      </span>
    )}
    <span className={styles.cardContent}>
      <CardTitle label={props.option.label} badge={props.option.badge} />
      <CardDetails {...props} />
    </span>
  </>
);

// Icon (and badge) on a top row above the title; with no icon there is no top row and the badge stays beside the title.
const StackedCardBody: FC<CardBodyProps> = (props) => {
  const { icon, label, badge } = props.option;
  return (
    <span className={styles.cardContent}>
      {icon ? (
        <>
          <span className={styles.cardTop}>
            <span className={styles.cardTopIcon} aria-hidden="true">
              {icon}
            </span>
            {badge}
          </span>
          <span className={styles.cardLabel}>{label}</span>
        </>
      ) : (
        <CardTitle label={label} badge={badge} />
      )}
      <CardDetails {...props} />
    </span>
  );
};

// Shared renderer for both the form-connected and standalone-controlled exports (not exported).
const RadioCardList = <V extends string>({
  options,
  selected,
  onSelect,
  label,
  required,
  disabled,
  columns = "auto",
  layout = "inline",
  reserveDescription,
}: {
  options: PunchupRadioCardOption<V>[];
  selected: V | null | undefined;
  onSelect: (value: V) => void;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  columns?: PunchupRadioCardColumns;
  layout?: PunchupRadioCardLayout;
  reserveDescription?: string;
}) => {
  const stacked = layout === "stacked";
  const CardBody = stacked ? StackedCardBody : InlineCardBody;
  return (
    <div className={styles.root}>
      {label && (
        <PunchupFieldLabel as="span" label={label} required={required} />
      )}
      <div
        className={cx(
          styles.cardList,
          columns === 1 && styles.cardListSingle,
          columns === 3 && styles.cardListTriple,
          stacked && styles.cardListStacked
        )}
        data-columns={columns}
      >
        {options.map((option) => {
          const isSelected = selected === option.value;
          return (
            <button
              key={option.value}
              type="button"
              disabled={disabled || option.disabled}
              // Selection state for assistive tech (rationale: CLAUDE.md → Selection a11y).
              aria-pressed={isSelected}
              className={cx(
                styles.card,
                stacked && styles.cardStacked,
                isSelected && styles.cardSelected
              )}
              onClick={() => onSelect(option.value)}
            >
              <span className={styles.radioIndicator} aria-hidden="true" />
              <CardBody
                option={option}
                reserveDescription={reserveDescription}
              />
              {option.aside && (
                <span className={styles.cardAside}>
                  {option.aside}
                  {option.asideCaption != null && (
                    <span className={styles.cardAsideCaption}>
                      {option.asideCaption}
                    </span>
                  )}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

/**
 * PunchupRadioCardGroup — react-hook-form connected.
 *
 * Radio selection rendered as a column of bordered cards (1-col; 2-col at ≥400px container width), each with
 * a label and optional description. Reads/writes the field via `useFormContext()` (`watch`/`setValue`);
 * requires a `FormProvider` ancestor. For local (non-form) state, use `PunchupRadioCards`. `"use client"`.
 */
export const PunchupRadioCardGroup: FC<PunchupRadioCardGroupProps> = ({
  name,
  options,
  label,
  required,
  disabled,
  columns,
  layout,
  reserveDescription,
}) => {
  const { watch, setValue } = useFormContext();
  // watch(name) is `any` (untyped FieldValues) — assert the field's known string type at the RHF boundary.
  const selected = watch(name) as string | undefined;
  return (
    <RadioCardList
      options={options}
      selected={selected}
      onSelect={(v) =>
        setValue(name, v, { shouldDirty: true, shouldValidate: true })
      }
      label={label}
      required={required}
      disabled={disabled}
      columns={columns}
      layout={layout}
      reserveDescription={reserveDescription}
    />
  );
};

/**
 * PunchupRadioCards — standalone controlled.
 *
 * The same card visual as `PunchupRadioCardGroup`, driven by `value`/`onChange` instead of react-hook-form.
 * Use when the selection is local page state (e.g. a mode toggle above a form) rather than a form field.
 */
export const PunchupRadioCards = <V extends string = string>({
  value,
  onChange,
  options,
  label,
  disabled,
  columns,
  layout,
  reserveDescription,
}: PunchupRadioCardsProps<V>) => (
  <RadioCardList
    options={options}
    selected={value}
    onSelect={onChange}
    label={label}
    disabled={disabled}
    columns={columns}
    layout={layout}
    reserveDescription={reserveDescription}
  />
);
